package ru.chm.gradereport.ui.components

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Soft hover/press wash instead of the default rectangular ripple.
 */
fun Modifier.softClickable(
    onClick: () -> Unit,
    highlight: Color,
    enabled: Boolean = true,
    shape: Shape = RoundedCornerShape(10.dp),
    hoverAlpha: Float = 0.18f,
    pressAlpha: Float = 0.28f,
): Modifier = composed {
    val interactionSource = remember { MutableInteractionSource() }
    val hovered by interactionSource.collectIsHoveredAsState()
    val pressed by interactionSource.collectIsPressedAsState()
    val alpha by animateFloatAsState(
        targetValue = when {
            !enabled -> 0f
            pressed -> pressAlpha
            hovered -> hoverAlpha
            else -> 0f
        },
        animationSpec = tween(140),
        label = "softClickAlpha",
    )
    this
        .clip(shape)
        .background(highlight.copy(alpha = alpha), shape)
        .clickable(
            interactionSource = interactionSource,
            indication = null,
            enabled = enabled,
            onClick = onClick,
        )
}

data class SoftHoverState(
    val interactionSource: MutableInteractionSource,
    val hovered: Boolean,
    val pressed: Boolean,
)

@Composable
fun rememberSoftHover(
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
): SoftHoverState {
    val hovered by interactionSource.collectIsHoveredAsState()
    val pressed by interactionSource.collectIsPressedAsState()
    return SoftHoverState(interactionSource, hovered, pressed)
}

fun Modifier.softHoverable(state: SoftHoverState): Modifier =
    hoverable(interactionSource = state.interactionSource)

@Composable
fun softFieldColors(
    state: SoftHoverState,
    idleBg: Color,
    hoverBg: Color,
    pressBg: Color = hoverBg,
    idleBorder: Color,
    hoverBorder: Color,
    pressBorder: Color = hoverBorder,
): Pair<Color, Color> {
    val bg by animateColorAsState(
        targetValue = when {
            state.pressed -> pressBg
            state.hovered -> hoverBg
            else -> idleBg
        },
        animationSpec = tween(160),
        label = "softFieldBg",
    )
    val border by animateColorAsState(
        targetValue = when {
            state.pressed -> pressBorder
            state.hovered -> hoverBorder
            else -> idleBorder
        },
        animationSpec = tween(160),
        label = "softFieldBorder",
    )
    return bg to border
}

fun Modifier.softFieldSurface(
    bg: Color,
    border: Color,
    shape: Shape = RoundedCornerShape(12.dp),
): Modifier = this
    .clip(shape)
    .background(bg, shape)
    .border(1.dp, border, shape)

val SoftDeleteColor = Color(0xFFEF5350)

/** Soft delete action — same wash/shape as «удалить» on attendance headers. */
@Composable
fun SoftDeleteText(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    fontSize: TextUnit = 14.sp,
    fontWeight: FontWeight = FontWeight.Normal,
) {
    Text(
        text = text,
        color = SoftDeleteColor,
        fontSize = fontSize,
        fontWeight = fontWeight,
        modifier = modifier
            .softClickable(
                onClick = onClick,
                highlight = SoftDeleteColor,
                shape = RoundedCornerShape(8.dp),
                hoverAlpha = 0.22f,
                pressAlpha = 0.34f,
            )
            .padding(horizontal = 8.dp, vertical = 4.dp),
    )
}

/** Filled red delete button — same size/shape as PrimaryButton. */
@Composable
fun SoftDeleteButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Button(
        onClick = onClick,
        modifier = modifier,
        colors = ButtonDefaults.buttonColors(
            containerColor = SoftDeleteColor,
            contentColor = Color.White,
        ),
        shape = RoundedCornerShape(12.dp),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 10.dp),
    ) {
        Text(
            text,
            fontWeight = FontWeight.SemiBold,
            maxLines = 1,
            softWrap = false,
            overflow = TextOverflow.Ellipsis,
        )
    }
}
